// Recruiting — My Desk panel (owner ask, 2026-09-25). Four pipeline counts
// plus the LMT roster headcount, reusing the exact same typed client
// (client/src/lib/recruitingApi.ts) and resource-cache key RecruitingBoard.tsx
// (People → Recruiting tab) already fetches GET /api/v1/recruiting/summary
// through — one cache entry, whichever surface asks for it first, never a
// second parallel fetch of the same data under a different key.
//
// Scoping is the DESK convention, not the People-tab one: RecruitingBoard's
// tabs use uppercase 'ALL_CLINICS' / 'ARCHIVE' ids for its own candidates
// query, but a My Desk panel's `config.clinic` is the same closed vocabulary
// every other `clinicConfigurable` panel type uses — a known clinic code, or
// lowercase `'all'` (src/desk.js's validatePanel; PayHoursPanel.tsx's own
// clinicsFor is the client-side twin of that same rule). `all` sums the four
// clinics — the summary endpoint already returns that sum pre-computed
// (`data.all`), so there is nothing to add up client-side.
//
// Owner preview (panelCatalog.tsx, 2026-09-24 convention): this file is only
// ever mounted once ownerPreview() is true — the catalog entry renders
// ComingSoonPanel for every other role until Tomer approves it, same as My
// Mail / My Calendar. This file does not check ownerPreview() itself.
//
// People has no URL-selected tab (client/src/pages/People.tsx's `extraTab`
// state reads only the `#pay-hours` / `#person-<id>` hash, never a query
// string), so the count links below land on plain `/people` rather than the
// Recruiting tab specifically — flagged in the build report, not fixed here
// (People.tsx belongs to another session).
import type { JSX } from 'preact';
import { useResource } from '../../lib/store';
import { visibleClinics } from '../ClinicTabs';
import { LOCATION_NAMES, type LocationCode } from '../../lib/locations';
import {
  fetchRecruitingSummary, RECRUITING_SUMMARY_KEY, fetchNewApplications, RECRUITING_NEW_APPLICATIONS_KEY,
  type RecruitingSummary, type RecruitingClinicSummary, type NewApplication, type NewApplicationsResponse,
} from '../../lib/recruitingApi';
import { DeskPanel } from '../DeskPanel';
import { NewApplicationsSection, newApplicationsInScope } from './NewApplicationsSection';
import { FaultState } from '../FaultState';

/** The one clinic this panel instance covers, or 'all' four summed — same
 * shape as PayHoursPanel.tsx's clinicsFor: an unset or no-longer-visible
 * config.clinic falls back to 'all' when the viewer can see more than one
 * clinic (owner/director), or to their own one when they can't (a manager —
 * the generic per-panel clinic picker in DeskGrid.tsx does not even render
 * for a MANAGER, so config.clinic is never set for one in practice). */
export function recruitingScopeFor(
  configClinic: string | undefined,
  visible: LocationCode[],
): LocationCode | 'all' {
  if (configClinic && configClinic !== 'all' && visible.includes(configClinic as LocationCode)) {
    return configClinic as LocationCode;
  }
  return visible.length === 1 ? visible[0] : 'all';
}

/** `data.all` already IS the four-clinic sum (src/recruiting.js) — nothing
 * to add up here, only which ready-made block this panel instance shows. A
 * scope naming a clinic the response has no block for (should not happen —
 * src/recruiting.js always fills every LOCATION_ORDER code — but "guards
 * over trust") falls back to `all` rather than showing nothing. */
export function recruitingBlockFor(data: RecruitingSummary, scope: LocationCode | 'all'): RecruitingClinicSummary {
  if (scope === 'all') return data.all;
  return data.clinics[scope] ?? data.all;
}

/** One clinic button on the panel (owner, 2026-10-06: "show the locations
 * buttons condensed showing new today, to review and interviews and if
 * there is someone new it should blink green on the edges"). "Someone new"
 * = a person who arrived today, or an applicant still waiting to be emailed
 * (the alert table above). Pure. */
export interface ClinicButton {
  code: LocationCode;
  name: string;
  newToday: number;
  toReview: number;
  interviews: number;
  waiting: number;
  hasNew: boolean;
}

export function clinicButtonsFor(
  data: RecruitingSummary,
  scope: LocationCode | 'all',
  visible: LocationCode[],
  waitingApps: NewApplication[],
): ClinicButton[] {
  const codes = scope === 'all' ? visible : [scope];
  return codes.map((code) => {
    const b = data.clinics[code];
    const newToday = b?.newToday ?? 0;
    const waiting = waitingApps.filter((a) => a.locationCode === code).length;
    return {
      code,
      name: LOCATION_NAMES[code],
      newToday,
      toReview: b?.stages.TO_REVIEW ?? 0,
      interviews: b?.stages.INTERVIEW ?? 0,
      waiting,
      hasNew: newToday > 0 || waiting > 0,
    };
  });
}

export function RecruitingDeskPanel(
  { config, chromeless = false }: { config?: { clinic?: string }; chromeless?: boolean } = {},
): JSX.Element | null {
  const load = useResource<RecruitingSummary>(RECRUITING_SUMMARY_KEY, fetchRecruitingSummary);
  const apps = useResource<NewApplicationsResponse>(RECRUITING_NEW_APPLICATIONS_KEY, fetchNewApplications);

  if (load.status === 'loading') {
    if (!chromeless) {
      return (
        <DeskPanel title="nvRecruiter" testId="recruiting-desk-panel">
          <p class="muted">Loading.</p>
        </DeskPanel>
      );
    }
    return null;
  }

  if (load.status === 'fault') {
    return (
      <DeskPanel title="nvRecruiter" testId="recruiting-desk-panel" chromeless={chromeless}>
        <FaultState name="recruiting_summary_load_failed" detail={load.error} />
      </DeskPanel>
    );
  }

  const visible = visibleClinics();
  const scope = recruitingScopeFor(config?.clinic, visible);
  const waitingApps = apps.status === 'ready' ? newApplicationsInScope(apps.data, scope, visible) : [];
  // Owner, 2026-10-12: a one-clinic viewer (a manager, or the owner viewing
  // as one clinic) is not told their own clinic's name — it is their clinic.
  const single = visible.length === 1;
  const subtitle = scope === 'all' || single ? undefined : LOCATION_NAMES[scope];

  return (
    <DeskPanel title="nvRecruiter" subtitle={subtitle} testId="recruiting-desk-panel" chromeless={chromeless}>
      {/* New Indeed applicants nobody has emailed yet — the alert sits on top
          of the counts (owner, 2026-09-30: one nvRecruiter table, not two). */}
      <NewApplicationsSection scope={scope} visible={visible} />
      <div class="rec-clinics" data-testid="recruiting-summary">
        {clinicButtonsFor(load.data, scope, visible, waitingApps).map((c) => (
          <a
            key={c.code}
            href={`/recruiting?clinic=${c.code}`}
            class={c.hasNew ? 'rec-clinic has-new' : 'rec-clinic'}
            title={c.waiting > 0 ? `${c.waiting} waiting to hear from you` : undefined}
            data-testid={`recruiting-clinic-${c.code}`}
          >
            {single ? null : <span class="rec-clinic-name">{c.name}</span>}
            <span class="rec-clinic-counts">
              <span data-testid={`recruiting-clinic-${c.code}-new`}><b>{c.newToday}</b> New today</span>
              <span data-testid={`recruiting-clinic-${c.code}-review`}><b>{c.toReview}</b> To review</span>
              <span data-testid={`recruiting-clinic-${c.code}-interviews`}><b>{c.interviews}</b> Interviews</span>
            </span>
          </a>
        ))}
      </div>
    </DeskPanel>
  );
}
